import React from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import MapComponent from "frontend/Components/GoogleMaps/GoogleMaps";
import "../../../../css/Common/Pages/ContactUsPage/ContactUsPage.css";
import { useLocation } from "react-router-dom";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, SEO_SITE, getPageUrl } from "frontend/Utils/seoConfig";

/**
 * ContactUsPage component renders the Contact Us page of the application.
 *
 * - Determines if the user is an adviser based on the location state.
 * - Configures navigation items dynamically depending on the user type (adviser or customer).
 * - Sets the page title and meta description using Helmet.
 * - Renders the NavBar, ContactUs form, complaints procedure information, a map, and the footer.
 *
 * @component
 * @returns {JSX.Element} The rendered Contact Us page.
 */
const ContactUsPage = () => {
  const location = useLocation();
  const isAdviser = location.state?.isAdviser || false;
  const navigationItems = [
    { label: "Home", navigateTo: isAdviser ? "/Adviser" : "/Customer" },
    {
      label: isAdviser ? "Join Us" : "Our Services",
      navigateTo: isAdviser ? "/Adviser/JoinUs" : "/OurServices",
    },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  const contactStructuredData = {
    "@type": "ContactPage",
    name: "Contact Rosemount Financial Solutions",
    description: SEO_PAGES.contactUs.description,
    url: getPageUrl("/ContactUs"),
    about: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
    mainEntity: {
      "@type": "Organization",
      "@id": `${SEO_SITE.siteUrl}/#organization`,
      name: SEO_SITE.siteName,
      url: SEO_SITE.siteUrl,
      telephone: "01256 405460",
      email: "complaints@rosemountfs.co.uk",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Rosemount House, 2-4 Chequers Road",
        addressLocality: "Basingstoke",
        postalCode: "RG21 7PU",
        addressCountry: "GB",
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "01256 405460",
          contactType: "customer support",
          areaServed: "GB",
          availableLanguage: "English",
        },
        {
          "@type": "ContactPoint",
          email: "complaints@rosemountfs.co.uk",
          contactType: "complaints",
          areaServed: "GB",
          availableLanguage: "English",
        },
      ],
    },
  };

  
  return (
    <div className="">
      <SeoHelmet
        page={SEO_PAGES.contactUs}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Contact Us",
            url: getPageUrl("/ContactUs"),
          },
        ]}
        extraSchemas={[contactStructuredData]}
      />
      <NavBar navigationItems={navigationItems} />
      <ContactUs isAdviser={isAdviser} />
      <div className="ContactUs-Content">
        <h2>Complaints Procedure</h2>
        <div className="ContantUs-Content-Container">
          <div className="Content">
            If you are not happy with our service, please write to Complaints
            Officer, Rosemount Financial Solutions (IFA) Ltd, Rosemount House,
            2-4 Chequers Road, Basingstoke, RG21 7PU. Telephone 01256 405460 or
            e-mail complaints@rosemountfs.co.uk  
          </div>

          <div className="Content">
            A summary of our internal complaints handling procedures for the
            reasonable and prompt handling of complaints is available on request
            and if you cannot settle your complaint with us, you may be entitled
            to refer it to the Financial Ombudsman Service
            at www.financial-ombudsman.org.uk  or by contacting them on 0800 023
            4567.
          </div>
        </div>
      </div>
      <MapComponent />
      <Footer isAdviser={isAdviser} />
    </div>
  );
};

export default ContactUsPage;
